import React, { useState, useEffect } from 'react';
import './App.css';
import { CreateRootStore } from './models/RootStore';
import { CurrentUserInitializer } from './CurrentUserInitializer';
import Splash from './components/Splash/Splash';
import Home from './pages/Home';
import HomeV2 from './pagesv2/HomeV2';
import { Route, Switch, Redirect } from 'react-router-dom';
import { observer } from 'mobx-react';
import ErrorDisplay from './components/ErrorDisplay/ErrorDisplay';
import { CompanyInitializer } from './CompanyInitializer';
import { useUserContext } from './UserContextProvider';
import { useAuth0 } from '@auth0/auth0-react';
import InactivityTimer from './components/InactivityTimer';
import LoginView from './Login';
import * as Sentry from '@sentry/react';
import './utils/devSeedTeamData'; // Dev-only: registers window.seedTeamData()


const App = observer(() => {
  const { isAuthenticated, user } = useAuth0();

  if (!isAuthenticated) {
    return <LoginView />;
  }

  Sentry.setUser({ id: user?.sub?.split('|')[1], email: user?.email });

  return <AppInternal user={user} />;
});


const AppInternal = ({ user }) => {
  const [rootStore] = useState(CreateRootStore());
  const { userId } = useUserContext();
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState('');
  const { logout, isAuthenticated } = useAuth0();
  const [sessionKey, setSessionKey] = useState(Date.now());

  // Reset sessionKey on every login
  useEffect(() => {
    if (isAuthenticated) {
      setSessionKey(Date.now());
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const initialize = async () => {
      try {
        const {
          usersStore,
          companyStore,
          assessmentsStore,
          adminStore,
          modulesStore
        } = rootStore;

        await CurrentUserInitializer(
          usersStore,
          adminStore,
          userId
        );

        let companyId = usersStore.currentUser.companyIds[0];

        if (!companyId) {
          console.error('User has no company IDs assigned. Contact your administrator.');
          setIsLoaded(true);
          return;
        }

        await CompanyInitializer(
          companyStore,
          assessmentsStore,
          usersStore,
          modulesStore,
          companyId
        );



        // Expose rootStore in dev for testing (browser console access)
        if (process.env.NODE_ENV === 'development') {
          window.__rootStore = rootStore;
        }

        setIsLoaded(true);
      } catch (error) {
        console.log('Error in initialization!');
        console.error(error);

       // logout(); 
      }
    };

    initialize();
  }, [logout, rootStore, setError, setIsLoaded, userId]);

  if (!isLoaded) {
    return <div data-testid='splash'>
      <ErrorDisplay message={error} />
      <Splash showLoader={true} />
    </div>;
  }

  return (
    <div style={{ height: '100%' }} data-testid='app'>
      <InactivityTimer key={user?.sub + '-' + sessionKey} logout={logout} /> {/* Remounts timer on every login */}
      <Switch>
        <Redirect from="/v2/:rest+" to="/:rest+" />
        <Redirect from="/v2" to="/" exact />
        <Route
          path='/v1'
          render={(props) => <Home {...props} rootStore={rootStore} />}
        />
        <Route
          path='/'
          render={(props) => <HomeV2 {...props} rootStore={rootStore} />}
        />
      </Switch>
    </div>
  );
};

export default App;
